<script setup lang="ts">
import { nextTick, onMounted, useTemplateRef,watch } from 'vue';
import { useI18n } from 'vue-i18n';

import type { Step } from '@/typings/forms/Step';

defineOptions({
  inheritAttrs: false,
});

defineProps<{
  steps: Step[];
}>();

const currentStep = defineModel<number>({ required: true });

const { t } = useI18n();

const activeStepRef = useTemplateRef<HTMLElement[]>('activeStep');

function scrollActiveStepIntoView() {
  const activeStep = activeStepRef.value?.[0];
  if (activeStep) {
    activeStep.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'nearest',
      inline: 'center'
    });
  }
}

watch(() => currentStep.value, () => {
  nextTick(() => {
    scrollActiveStepIntoView();
  });
});

onMounted(() => {
  scrollActiveStepIntoView();
});

function isCurrentStep(index: number): boolean {
  return index + 1 === currentStep.value;
}

function isPastStep(index: number): boolean {
  return index + 1 < currentStep.value;
}

function getStepWrapperInfo(index: number, step: Step) {
  const isPast = isPastStep(index);
  const hasUrl = !!step.url;
  const hasCallback = !!step.callback;
  
  const isLink = isPast && hasUrl;
  const isButton = isPast && hasCallback;
  
  return {
    isLink,
    url: isLink ? step.url : undefined,
    attrs: {
      class: 'stepNavigation-step',
      tabindex: isButton ? 0 : undefined,
      role: isButton ? 'button' : undefined,
      onClick: isButton ? () => step.callback!() : undefined
    }
  };
}
</script>

<template>
  <div class="stepNavigation-container">
    <nav class="stepNavigation"
         aria-label="Progression des étapes"
         v-bind="$attrs">
      <ol class="stepNavigation-list">
        <li v-for="(step, index) in steps"
            :key="index"
            :ref="isCurrentStep(index) ? 'activeStep' : undefined"
            class="stepNavigation-item"
            :data-step-completion-status="isPastStep(index) ? 'completed' : isCurrentStep(index) ? 'active' : 'incomplete'"
            :aria-current="isCurrentStep(index) ? 'step' : undefined">
          <component :is="getStepWrapperInfo(index, step).isLink ? 'a' : 'div'"
                     v-bind="getStepWrapperInfo(index, step).attrs"
                     :href="getStepWrapperInfo(index, step).url">
            <div class="stepNavigation-chip">
              <span>{{ !isPastStep(index) ? index + 1 : "✓" }}</span>
            </div>

            <div class="stepNavigation-label">
              {{ t(`stepNavigation.steps.${step.label}`) }}
            </div>
          </component>
        </li>
      </ol>
    </nav>
  </div>
</template>

<style lang="scss">
.stepNavigation {
  --completedPrimaryColor: var(--gray-950);
  --completedContrastColor: var(--white);
  --activePrimaryColor: var(--green-600);
  --activeContrastColor: var(--white);
  --incompletePrimaryColor: var(--gray-200);
  --incompleteContrastColor: var(--gray-500);
  width: fit-content;
  padding: var(--spc28-24);
  margin-inline: auto;
  
  &-container {
    container: stepNavigation-container / inline-size;
    border-bottom: 1px solid var(--gray-200);
  }

  &-list {
    --numberOfSteps: 5;
    --numberOfVisibleSteps: var(--numberOfSteps);
    --gap: var(--spc24-16);
    display: grid;
    grid-template-columns: repeat(var(--numberOfVisibleSteps), 1fr);
    justify-content: space-between;
    gap: var(--gap);
    
    padding: 0;
    margin: 0;

    list-style: none;
    counter-reset: step-counter;

    overflow-x: auto;
    scroll-snap-type: both mandatory;
    overscroll-behavior: contain;

    > * {
      flex: 1 0 0;
      scroll-snap-align: start;
      scroll-snap-stop: always;
    }
  }

  &-item {
    position: relative;
    padding-left: 0;
    margin-bottom: 0;
    text-align: start;
  }

  &-step {
    display: flex;
    align-items: first baseline;
    gap: 8px;

    text-decoration: none;
    color: unset;
  }

  &-chip {
    --chipSize: 1.5em;
    --chipColorBackground: var(--incompletePrimaryColor);
    --chipColorContrast: var(--incompleteContrastColor);
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    width: var(--chipSize);
    height: var(--chipSize);

    border-radius: 50%;
    background-color: var(--chipColorBackground);

    font-weight: 600;
    color: var(--chipColorContrast);

    svg {
      height: .6em;
    }
  }

  &-label {
    font-weight: 500;
    line-height: 1.3;
  }

  &-button {
    background: none;
    border: none;
    cursor: pointer;
    margin: 0;
    padding: 0;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
  }

  &-item[data-step-completion-status='completed'] .stepNavigation-chip {
    --chipColorBackground: var(--completedPrimaryColor);
    --chipColorContrast: var(--completedContrastColor);
  }

  &-item[aria-current="step"] .stepNavigation-chip {
    --chipColorBackground: var(--activePrimaryColor);
    --chipColorContrast: var(--activeContrastColor);
  }
}

a.stepNavigation-step {
  display: flex;
  align-items: first baseline;
  text-decoration: none;
  color: unset;
}

@media screen and (max-width: 1024px){
  .stepNavigation{
    padding: 2.0rem;
  }

  .stepNavigation-step{
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .stepNavigation-label{
      text-align:center;
    }
  }
}
@media screen and (max-width: 767px){
  .stepNavigation-step{
    .stepNavigation-label{
      display:none;
    }
  }
}
</style>
